import { css, html, LitElement, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { customElement, property } from 'lit/decorators.js';
import { createRef, ref, Ref } from 'lit/directives/ref.js';
import resetStyle from '../../shared/components/html-content/reset.style';
import { overrideDesignTokens } from '../../shared/styles/override-design-tokens';
import { ludsBaseFont } from '../../shared/styles/typography';
import '../button/button';
import '../icon/icon';
import { BackdropManager, BackdropClient } from './modal-backdrop.service';
import { ModalVariants } from '../../shared/types/modal.model';

type WidthSize = 'sm' | 'md' | 'lg' | 'xl';
type HeightSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalEventHandler {
  selector: string;
  event: string;
  handler: (event: Event) => void;
}

/**
 * Modal component
 */
@customElement('luds-modal')
export class LudsModal extends LitElement implements BackdropClient {
  static override styles = [
    resetStyle,
    ludsBaseFont,
    overrideDesignTokens,
    css`
      :host(.luds-modal-fixed) {
        position: fixed;
        inset: 0;
        z-index: var(--modal-z-index, 1000);
        pointer-events: none;
      }

      dialog {
        height: fit-content;
        max-height: 90vh;
        padding: var(--spacing-spacing-16) var(--spacing-spacing-24);
        inset: 0;
        border: none;
        border-radius: 8px;
        box-shadow: 3px 4px 10px 0px rgba(0, 0, 0, 0.2);
        z-index: var(--modal-z-index, 1000);
        pointer-events: auto;
        overflow-y: auto;
      }

      dialog::backdrop {
        background: transparent;
        backdrop-filter: none;
      }

      .dialog-content {
        min-height: 100%;
        position: relative;
        display: flex;
        flex-direction: column;
      }

      .dialog-header {
        padding: var(--spacing-spacing-16) var(--spacing-spacing-16) var(--spacing-spacing-12) var(--spacing-spacing-24);
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: var(--spacing-spacing-16);
      }

      .dialog-footer {
        padding: var(--spacing-spacing-12);
      }

      .no-border {
        border: none;
        display: none;
      }

      .dialog-title {
        margin: 0;
        font-size: 1.8rem;
        font-weight: 500;
      }

      .dialog-header-actions {
        display: flex;
        align-items: center;
        gap: var(--spacing-spacing-04);
      }

      slot[name='additional-header-actions']::slotted(*) {
        border-bottom: 1px solid var(--semantic-colour-tokens-stroke-stroke-light, #e4e7eb);
        border-top: 1px solid var(--semantic-colour-tokens-stroke-stroke-light, #e4e7eb);
        text-align: right;
        padding: var(--spacing-spacing-16) var(--spacing-spacing-24);
      }

      .dialog-body {
        padding: var(--spacing-spacing-16) var(--spacing-spacing-24) var(--spacing-spacing-32) var(--spacing-spacing-24);
        flex-grow: 1;
        overflow: auto;
      }

      .dialog-body p,
      ::slotted(p) {
        font-size: 1.4rem !important;
      }

      .dialog-body--chatbox {
        padding: var(--spacing-spacing-16) var(--spacing-spacing-24);
        background-color: var(--semantic-colour-tokens-surface-surface-secondary, #f7f9fa);
      }

      .dialog-close {
        position: absolute;
        z-index: 1;
        right: var(--spacing-spacing-08);
        top: var(--spacing-spacing-16);
      }

      .resizable {
        resize: both;
        overflow: auto;
      }

      ::slotted(.dialog-footer) {
        display: flex;
        justify-content: space-between;
        padding: var(--spacing-spacing-12);
      }

      @keyframes showDialogAnimation {
        0% {
          transform: translateY(100%);
          opacity: 0;
        }
        100% {
          transform: translateY(0%);
          opacity: 1;
        }
      }

      .show-dialog-animation {
        animation: 1s ease-in-out showDialogAnimation;
      }

      @media (max-width: 840px) {
        .width-sm,
        .width-md,
        .width-lg,
        .width-xl {
          width: calc(100% - var(--spacing-spacing-24));
        }
      }

      @media (min-width: 841px) and (max-width: 1024px) {
        .width-sm {
          width: 50%;
        }
        .width-md {
          width: 75%;
        }
        .width-lg,
        .width-xl {
          width: 100%;
        }
      }

      @media (min-width: 1025px) and (max-width: 1280px) {
        .width-sm {
          width: 42%;
        }
        .width-md {
          width: 50%;
        }
        .width-lg {
          width: 83%;
        }
        .width-xl {
          width: 100%;
        }
      }

      @media (min-width: 1281px) {
        .width-sm {
          width: 42%;
        }
        .width-md {
          width: 50%;
        }
        .width-lg {
          width: 83%;
        }
        .width-xl {
          width: 100%;
        }
      }

      //TODO see if this will get removed since we are no longer doing fixed hegihts
      .height-sm {
        height: fit-content;
        max-height: 80vh;
      }

      .height-md {
        height: fit-content;
        max-height: 80vh;
      }

      .height-lg {
        height: fit-content;
        max-height: 80vh;
      }

      .height-xl {
        height: fit-content;
        max-height: 100vh;
      }

      .chat-box {
        position: fixed;
        inset: unset;
        right: var(--spacing-spacing-24);
        bottom: var(--spacing-spacing-24);

        transition: height 0.3s ease-in-out;
      }

      :host([variant='chatbox']) .chat-box {
        width: calc(
          100% - var(--spacing-spacing-48)
        ); /** -48px because element is going outside of the display because of the fixed position */
        height: 50%;
      }

      :host([variant='chatbox']) .chat-box--expanded {
        height: 80%;
      }

      @media (min-width: 767px) and (max-width: 1023px) {
        :host([variant='chatbox']) .chat-box {
          width: 50%;
        }
      }

      @media (min-width: 1024px) {
        :host([variant='chatbox']) .chat-box {
          width: 33%;
        }
      }
    `,
  ];

  @property({ type: Boolean })
  open = false;

  @property({ type: String })
  override title = '';

  @property({ type: Boolean })
  animation = false;

  @property({ type: Boolean })
  resizable = false;

  /** Controls whether to show backdrop */
  @property({ type: Boolean })
  useBackdrop = true;

  @property({ type: String })
  customClass = '';

  /** Controls visibility of the resize button in modal header */
  @property({ type: Boolean })
  showResizeOption = false;

  /** Z-index for modal stacking */
  @property({ type: Number })
  zIndex = 0;

  @property({ type: Boolean })
  showCloseIcon = true;

  @property({ type: String })
  width: WidthSize | number = 'md';

  @property({ type: String })
  height: HeightSize | number = 'md';

  @property({ type: Boolean })
  showCustomHeader = false;

  @property({ type: Boolean })
  showCustomFooter = false;

  @property({ type: Array })
  eventHandlers: ModalEventHandler[] = [];

  @property({ type: String }) variant: ModalVariants = 'modal';

  /** Callback function to execute when modal closes */
  @property({ attribute: false })
  onCloseCallback: (() => void) | undefined;

  dialogRef: Ref<HTMLDialogElement> = createRef();

  private initialDimensions: { width: string; height: string } | null = null;
  private static globalZIndex = 1000;

  private initialWidth = '';
  private initialHeight = '';
  private cleanupHandlers: Array<() => void> = [];
  private backdropManager = BackdropManager.getInstance();

  override firstUpdated() {
    this.setInitialModalDimensions();
    this.attachEventHandlers();
    this.attachCustomEscapeClose();
  }

  private attachCustomEscapeClose() {
    const dialog = this.dialogRef.value;
    if (!dialog) return;

    // Prevent native dialog close on Escape
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault(); // Stop the native close behavior
      this.close(); // Use our close method instead
    });
  }

  override updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);

    if (changedProperties.has('open')) {
      if (this.open) {
        this.backdropManager.addModal(this);
        requestAnimationFrame(() => {
          if (this.dialogRef.value && !this.style.getPropertyValue('--modal-z-index')) {
            this.show();
          }
        });
      } else {
        this.backdropManager.removeModal(this);
      }
    }

    if (changedProperties.has('useBackdrop')) {
      this.backdropManager.updateBackdrop();
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.backdropManager.removeModal(this);
    this.cleanup();
  }

  private attachEventHandlers() {
    this.cleanup();

    if (!this.eventHandlers?.length) return;

    requestAnimationFrame(() => {
      this.eventHandlers.forEach(this.attachSingleHandler.bind(this));
    });
  }

  private attachSingleHandler({ selector, event, handler }: ModalEventHandler) {
    const element = this.findElement(selector);
    if (!element) return;

    element.addEventListener(event, handler);
    this.cleanupHandlers.push(() => element.removeEventListener(event, handler));
  }

  private findElement(selector: string): Element | null {
    return this.shadowRoot?.querySelector(selector) || this.querySelector(selector);
  }

  private cleanup() {
    this.cleanupHandlers.forEach((cleanup) => cleanup());
    this.cleanupHandlers = [];
  }

  close() {
    const event = new CustomEvent('luds-dialog-close', {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: this,
    });

    this.dispatchEvent(event);

    if (event.defaultPrevented) {
      return;
    }

    this.open = false;
    const dialog = this.dialogRef.value;

    if (dialog?.open) {
      dialog.close();
      dialog.style.zIndex = '';
    }

    if (this.initialDimensions) {
      this.resetAfterResize();
    }

    this.classList.remove('luds-modal-fixed');

    // Restore background scrolling only if no other modals are open
    const openModals = Array.from(document.querySelectorAll('luds-modal')).filter(
      (modal) => modal !== this && modal.classList.contains('luds-modal-fixed')
    );

    if (openModals.length === 0) {
      document.body.style.overflow = '';
    }

    // Reset the modal's z-index property
    this.style.removeProperty('--modal-z-index');
    this.zIndex = 0;

    if (typeof this.onCloseCallback === 'function') {
      this.onCloseCallback();
    }

    // Auto-remove dynamically created modals
    if (!this.id && this.parentNode) {
      setTimeout(() => {
        this.parentNode?.removeChild(this);
      }, 100);
    }

    event.stopPropagation();
  }

  show() {
    if (!this.dialogRef.value) return;

    const dialog = this.dialogRef.value;

    if (dialog.open && typeof dialog.close === 'function') {
      dialog.close();
    }

    const openModals = Array.from(document.querySelectorAll('luds-modal')).filter(
      (modal) => modal !== this && modal.classList.contains('luds-modal-fixed')
    );

    if (openModals.length > 0) {
      this.zIndex = 20000 + ++LudsModal.globalZIndex;
    } else {
      this.zIndex = 1000 + ++LudsModal.globalZIndex;
    }

    // Prevent background scrolling
    document.body.style.overflow = 'hidden';

    this.style.setProperty('--modal-z-index', String(this.zIndex));
    this.classList.add('luds-modal-fixed');

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    }

    requestAnimationFrame(() => {
      dialog.scrollTop = 0;
      const dialogBody = dialog.querySelector('.dialog-body');
      if (dialogBody) {
        dialogBody.scrollTop = 0;
      }
    });

    this.dispatchEvent(
      new CustomEvent('luds-dialog-show', {
        bubbles: true,
        composed: true,
        detail: dialog,
      })
    );
  }

  toggleFullScreen() {
    const dialog = this.dialogRef.value;
    if (!dialog) return;

    const fullScreenWidth = '100vw';
    const fullScreenHeight = '100vh';
    const { width, height } = dialog.style;
    const isFullScreen = width === fullScreenWidth && height === fullScreenHeight;

    dialog.style.width = isFullScreen ? this.initialWidth : fullScreenWidth;
    dialog.style.height = isFullScreen ? this.initialHeight : fullScreenHeight;

    if (!isFullScreen) {
      this.initialWidth = width;
      this.initialHeight = height;
    }
  }

  resizeModal() {
    const dialog = this.dialogRef.value;
    if (!dialog) return;

    const isExpanded = !this.initialDimensions;

    if (isExpanded) {
      this.initialDimensions = {
        width: dialog.style.width || '',
        height: dialog.style.height || '',
      };

      // Remove chatbox classes and add large size classes
      this.updateDialogClass(['chat-box', 'chat-box--expanded'], ['width-lg', 'height-lg']);

      dialog.style.width = '';
      dialog.style.height = '';

      // Enable backdrop and reopen as modal
      this.useBackdrop = true;
      this.classList.add('luds-modal-fixed');
    } else {
      // Restore to chatbox mode
      this.resetAfterResize();
    }
  }

  private resetAfterResize() {
    const dialog = this.dialogRef.value;
    if (!dialog || !this.initialDimensions) return;

    this.updateDialogClass(['width-lg', 'height-lg'], ['chat-box']);

    dialog.style.width = this.initialDimensions.width;
    dialog.style.height = this.initialDimensions.height;

    this.initialDimensions = null;

    this.useBackdrop = false;
    this.classList.remove('luds-modal-fixed');
  }

  private updateDialogClass(toRemove: string[], toAdd: string[]) {
    const dialog = this.dialogRef.value;
    if (!dialog) return;

    dialog.classList.remove(...toRemove);
    dialog.classList.add(...toAdd);
  }

  private setModalWidth(dialog: HTMLDialogElement): void {
    const isStandardSize = ['sm', 'md', 'lg', 'xl'].includes(String(this.width));

    if (isStandardSize) {
      dialog.classList.add(`width-${this.width}`);
      return;
    }

    dialog.style.width = `${this.width}px`;
    window.console.warn(
      `Modal width value: ${this.width} is not aligned with luds design system. Expected values are 'sm', 'md', 'lg' and 'xl'`
    );
  }

  private setModalHeight(dialog: HTMLDialogElement): void {
    const isStandardSize = ['sm', 'md', 'lg', 'xl'].includes(String(this.height));

    if (isStandardSize) {
      dialog.classList.add(`height-${this.height}`);
      return;
    }

    dialog.style.height = `${this.height}px`;
    window.console.warn(
      `Modal height value: ${this.height} is not aligned with luds design system. Expected values are 'sm', 'md', 'lg' and 'xl'`
    );
  }

  private setInitialModalDimensions(): void {
    const dialog = this.dialogRef.value;
    if (!dialog) return;

    const sizesToRemove = [
      'width-sm',
      'width-md',
      'width-lg',
      'width-xl',
      'height-sm',
      'height-md',
      'height-lg',
      'height-xl',
    ];
    dialog.classList.remove(...sizesToRemove);

    this.setModalWidth(dialog);
    this.setModalHeight(dialog);
  }

  private renderAdditionalHeaderActions() {
    return html`<slot name="additional-header-actions"></slot>`;
  }

  private renderCustomDialogHeader() {
    return html`
      <div class="dialog-header">
        <slot name="custom-dialog-header"></slot>
        ${this.showCloseIcon
          ? html`
              <luds-button
                class="dialog-close"
                variant="ghost"
                size="small"
                icon="close"
                @click="${this.close}"
                data-qa-id="modalClose">
              </luds-button>
            `
          : nothing}
      </div>
    `;
  }

  private renderDialogHeader() {
    return html`
      <div class="dialog-header">
        <h2 class="dialog-title">${this.title}</h2>
        <div class="dialog-header-actions">
          ${this.showResizeOption
            ? html`
                <luds-button
                  class="mr-4"
                  variant="ghost"
                  size="small"
                  icon="maximise"
                  data-qa-id="modalResize"
                  @click="${this.resizeModal}">
                </luds-button>
              `
            : nothing}
          ${this.showCloseIcon
            ? html`
                <luds-button variant="ghost" size="small" icon="close" @click="${this.close}" data-qa-id="modalClose">
                </luds-button>
              `
            : nothing}
        </div>
      </div>
    `;
  }

  private renderCustomDialogFooter() {
    return html`<slot name="custom-dialog-footer"></slot>`;
  }

  private renderDialogFooter() {
    return html`<div class="dialog-footer">
      <slot name="footer"></slot>
    </div>`;
  }

  override render() {
    const { open, resizable, animation, variant, useBackdrop, customClass } = this;

    const modalBodyClasses = {
      'show-dialog-animation': !!animation,
      resizable: !!resizable,
      'chat-box': variant === 'chatbox',
      'chat-box--expanded': customClass?.includes('chat-box--expanded'),
      [customClass]: !!customClass,
    };

    const modalDialogBodyClasses = {
      'dialog-body--chatbox': variant === 'chatbox',
      'dialog-body': true,
    };

    return html`
      <dialog ?open=${open} ${ref(this.dialogRef)} class="${classMap(modalBodyClasses)}">
        <div class="dialog-content">
          ${this.showCustomHeader ? this.renderCustomDialogHeader() : this.renderDialogHeader()}
          ${this.renderAdditionalHeaderActions()}
          <div class="${classMap(modalDialogBodyClasses)}">
            <slot></slot>
          </div>
          ${this.showCustomFooter ? this.renderCustomDialogFooter() : this.renderDialogFooter()}
        </div>
      </dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'luds-modal': LudsModal;
  }
}
